<template>
  <div>
    <wc-page-title :loading='loading' title='Dashboard'>
      <template v-slot:rightSide>
        <div>
          <v-btn icon @click="showHelpDialog = true">
            <v-icon color='grey'>mdi-help-circle-outline</v-icon>
          </v-btn>
        </div>
      </template>
    </wc-page-title>
    <v-container>
      <wc-data-box
        :title='(hideCompleted ? "Open tasks" : "All Tasks") + (hideTabs ? "" : " for:")'
        :showAdd='true'
        @add='createTask'
      >
        <template v-slot:appendTitle>
          <span class='ml-6 caption grey--text font-italic'>{{ hideCompleted ? "(completed tasks currently hidden)" : ""}}</span>
        </template>
        <template v-slot:prependToolbarButtons>
          <v-tooltip open-delay='500' top>
            <template v-slot:activator='{ on }'>
              <v-btn small icon class='ml-2' v-on='on' @click='hideTabs = !hideTabs'>
                <v-icon color='grey lighten-1'>{{ hideTabs ? "mdi-tab" : "mdi-format-list-group" }}</v-icon>
              </v-btn>
            </template>
            <span class='caption'>{{ hideTabs ? "Switch to tab view" : "Switch to list view" }}</span>
          </v-tooltip>

          <v-tooltip open-delay='500' top>
            <template v-slot:activator='{ on }'>
              <v-btn small icon class='ml-2 mr-1' v-on='on' @click='hideCompleted = !hideCompleted'>
                <v-icon color='grey lighten-1'>{{ hideCompleted ? "mdi-clipboard-check-outline" : "mdi-clipboard-off-outline" }}</v-icon> 
              </v-btn>
            </template>
            <span class='caption'>{{ hideCompleted ? "Show Completed" : "Hide Completed" }}</span>
          </v-tooltip>
        </template>

        <v-tabs
          v-if='!hideTabs'
          background-color='grey darken-3'
          center-active
          dark
          height='32'
          v-model='tab'
        >
          <v-tab
            v-for="user in users"
            :key="user.id"
          >
            {{ user.first_name }}
          </v-tab>
        </v-tabs>

          <v-tabs-items v-model='tab'>

            <v-data-table
              :items='filteredTasks'
              :headers='hideTabs ? tableHeaders : tabsHeaders'
              dense
              :group-by="hideTabs ? 'assigned_to_obj.name_display' : []"
              :show-group-by='hideTabs'
              @click:row='openTask'
              :no-data-text='hideCompleted ? "No incomplete tasks found" : "No tasks found"' 
            >

              <template v-if='hideTabs' v-slot:group.header='{ group, groupBy, headers, toggle, remove, isOpen }'>
                <td :colspan='headers.length'>
                  <v-btn icon x-small class='mr-2' @click='toggle'><v-icon>{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down"}}</v-icon></v-btn>
                  {{ tableHeaders.find(h => h.value == groupBy).text }}: <span class='font-weight-bold'>{{ group }}</span>
                  <v-btn icon x-small class='ml-2' @click='remove'><v-icon>mdi-close</v-icon></v-btn>
                </td>
              </template>

              <template v-slot:item.status='{ item }'>
                <v-tooltip top>
                  <template v-slot:activator='{ on }'>
                    <v-icon v-on='on' :color='getStatus(item).color'>
                      {{ getStatus(item).icon }}
                    </v-icon>
                  </template>
                  <span class='caption'>{{ getStatus(item).tooltip }}</span>
                </v-tooltip>
              </template>
              <template v-slot:item.description='{ item }'>
                <v-tooltip top>
                  <template v-slot:activator='{ on }'>
                    <v-icon v-if='item.event' v-on='on'>mdi-calendar-cursor</v-icon>
                  </template>
                  <span class='caption'>Event Task</span>
                </v-tooltip>
                {{ item.description }}
              </template>
              <template v-slot:item.priority='{ item }'>
                <span v-if='item.priority' class='caption mr-1'>{{ getPriority(item).text }}</span>
                <span v-else class="grey--text text--lighten-2 font-italic">not set</span>
              </template>
              <template v-slot:item.date_due='{ item }'>
                <span v-if='item.date_due' :class='calcDueColor(item)'>{{ item.date_due | shortDateWithDay }}
                  <span class='caption' v-if='item.time_due'>{{ item.time_due | time }}</span>
                </span>
                <span v-else class="grey--text text--lighten-2 font-italic">not set</span>
              </template>
              <template v-slot:item.assigned_to='{ item }'>
                {{ item.assigned_to_obj ? item.assigned_to_obj.name_display : item.assigned_to }}
              </template>
              <template v-slot:item.task_manager='{ item }'>
                <span :class='item.task_manager_obj ? "" : "grey--text text--lighten-2 font-italic"'>
                  {{ item.task_manager_obj ? item.task_manager_obj.name_display : "not set" }}
                </span>
              </template>
            </v-data-table>

          </v-tabs-items>


      </wc-data-box>

      <task-dialog
        v-if='taskCopy'
        :loading='loading'
        :showDialog='showDialog'
        :taskCopy.sync="taskCopy"
        :profiles='profiles'
        @save='save'
        @cancel='cancel'
        @deleteTask='deleteTask'
      ></task-dialog>

      <wc-dialog
        :showDialog='showHelpDialog'
        title='Help'
        icon=''
        iconColor=''
        width='800'
        hideCancel
        saveText='Ok'
        @save='showHelpDialog = false'
      >
        <v-card-text v-html='helpText'></v-card-text>
      </wc-dialog>

    </v-container>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import formRules from '@/mixins/formRules'
import TaskDialog from '@/modules/tasks/components/TaskDialog'

export default {
  name: 'tasks-dashboard',
  mixins: [formRules],
  props: {
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    tasks: {
      required: true,
      type: Array
    },
    profiles: {
      required: true,
      type: Array
    }
  },
  data: () => ({
    showDialog: false,
    showHelpDialog: false,
    hideCompleted: true,
    hideTabs: true,
    tab: 0,
    tableHeaders: [
      { text: '', value: 'status', width: "56", groupable: false, sortable: false },
      { text: 'Priority', value: 'priority', width: "95", groupable: false },
      { text: 'Task Description', value: 'description', groupable: false },
      { text: 'Due By', value: 'date_due', width: "200", groupable: false }, 
      { text: 'Assigned to', value: 'assigned_to_obj.name_display', width: "200" },
      { text: 'Task Manager', value: 'task_manager', width: "200", groupable: false }
    ],
    tabsHeaders: [
      { text: '', value: 'status', width: "56", groupable: false, sortable: false },
      { text: 'Priority', value: 'priority', width: "95", groupable: false },
      { text: 'Task Description', value: 'description', groupable: false },
      { text: 'Due By', value: 'date_due', width: "200", groupable: false }, 
      { text: 'Task Manager', value: 'task_manager', width: "200", groupable: false }
    ],
    resetTask: {},
    taskCopy: null,
    helpText: "Welcome to the Tasks Area<br><br>"+
    "This area can be used to assign non-billable tasks to employees for completion.<br>"+
    "If assigned to a task, the assignee will get a profile alert badge at the top right of the CPM."+
    "<br><br>Some tasks will be created from events on the Office Reminders Calendar."+
    "<br>Users who have access to the Office calendar will be able to create tasks from there"+
    "<br><br>Anyone who has access to this page can create and edit tasks (any/all of them)"+
    "<br>On each users my-tasks page (accessed through the top right profile button) they will see a list of tasks assigned to them, and tasks that they're managing."+
    "<br>Managers can edit details on that page, but the assignee can not. (event tasks cannot be edited on that page)<br><br>"+
    "Completed tasks are hidden by default, but you can display them using the toggle beside the plus/add"
  }),
  computed: {
    now () {
      return moment()
    },
    filteredTasks () {
      let tasks = this.hideTabs ? this.tasks : _.filter(this.tasks, t => t.assigned_to_obj.id == this.users[this.tab].id)
      if (this.hideCompleted) {
        return _.filter(tasks, t => t.is_complete == false)
      }
      return tasks
    },
    users () {
      return _.uniqBy( _.map(this.tasks, "assigned_to_obj"), "id" )
    }
  },
  methods: {
    getPriority(item) {
      let tmp = this.$store.state.misc.globalOptions.taskPriorities.find(p => p.value === item.priority)
      if (tmp) {
        return tmp
      }
      return { text: "", value: null }
    },
    getStatus (item) {
      if (!item.id) {
        return this.$icons.task_new
      } else if (!item.read) {
        return this.$icons.task_unread
      } else if (item.is_complete) {
        return this.$icons.task_complete
      } else if (item.is_attempted) {
        return this.$icons.task_attempted
      } else if (item.in_progress) {
        return this.$icons.task_progress
      } else {
        return this.$icons.task_read
      }
    },
    calcDueColor(item) {
      if ( !item.is_complete && item.time_due && moment(item.date_due + 'T' + item.time_due).isBefore(this.now) ) {
        return "error--text"
      } else if ( !item.is_complete && moment(item.date_due).isSame(this.now, 'day') ) {
        return "warning--text"
      } else if ( !item.is_complete && moment(item.date_due).isBefore(this.now, 'day') ) {
        return "error--text"
      } else {
        return ""
      }
    },
    createTask() {
      this.taskCopy = this.resetTask
      // this.taskProgress = null
      this.taskCopy.created_by = this.$store.state.auth.profile.id
      this.taskCopy.date_created = moment().format('YYYY-MM-DDTHH:mm:ss')
      this.showDialog = true
    },
    openTask(task) {
      this.taskCopy = _.cloneDeep(task)
      if (!task.read && task.assigned_to == this.$store.state.auth.profile.id) {
        task.read = true
        this.taskCopy.read = true
        this.$emit('update', task)
      }
      this.$nextTick(() => {
        this.showDialog = true
      })
    },
    save(){
      if (this.taskCopy.id) {
        this.$emit('update', this.taskCopy)
      } else {
        this.$emit('create', this.taskCopy)
      }
      this.showDialog = false
    },
    cancel () {
      this.showDialog = false
    },
    deleteTask () {
      this.$emit('deleteTask', this.taskCopy)
      this.showDialog = false
    }
  },
  components: {
    'task-dialog': TaskDialog
  }
}
</script>